// ---------------------------------------------------------------------------
// Passos manuais do onboarding — config declarativa (redesign 2026-07-14).
//
// O design manda os passos manuais aparecerem NA PRÓPRIA ETAPA, quando ela
// fica ativa (painel dentro do card do pipeline), com edição retroativa via
// modal aberto do rail. Esta config alimenta os dois: mapeia cada etapa do
// pipeline aos seus campos (tipo/label/options) e centraliza a conversão
// draft ⇄ patch (date em "YYYY-MM-DD" cru, '' ⇄ null).
// ---------------------------------------------------------------------------

import {
   ONBOARDING_STATUS,
   BACKGROUND_CHECK_STATUS_OPTIONS,
   BACKGROUND_CHECK_SUB_ANALYSES,
   ADMISSION_EXAM_STATUS_OPTIONS,
} from '../constants';

// Campos por etapa. `key` = chave do draft E do backend (exceto date, que usa
// `draftKey` próprio pro <input type="date"> e converte no patch).
export const MANUAL_STEP_FIELDS = {
   // F2 (handoff 0067): as 2 sub-análises. REPROVADO em qualquer uma ⇒ o
   // backend desqualifica na MESMA resposta do manual-steps (200 já
   // DESQUALIFICADO) — o front só re-renderiza via onSaved.
   //
   // Layout em DUAS COLUNAS (uma por análise): statuses na 1ª linha, notes na
   // 2ª — a ordem dos campos + col:6 fazem cada análise ocupar uma coluna.
   [ONBOARDING_STATUS.BACKGROUND_CHECK]: [
      ...BACKGROUND_CHECK_SUB_ANALYSES.map((analysis) => ({
         key: analysis.statusKey,
         label: analysis.label,
         type: 'select',
         options: BACKGROUND_CHECK_STATUS_OPTIONS,
         col: 6,
      })),
      ...BACKGROUND_CHECK_SUB_ANALYSES.map((analysis) => ({
         key: analysis.notesKey,
         label: `Observações — ${analysis.shortLabel.toLowerCase()}`,
         type: 'textarea',
         col: 6,
         placeholder:
            analysis.statusKey === 'litigation_screening_status'
               ? 'Fontes consultadas, processos, ressalvas…'
               : 'Empresas contatadas, retorno das referências…',
      })),
   ],
   [ONBOARDING_STATUS.EXAME_ADMISSIONAL]: [
      {
         key: 'admission_exam_status',
         label: 'Resultado do exame',
         type: 'select',
         options: ADMISSION_EXAM_STATUS_OPTIONS,
      },
   ],
   [ONBOARDING_STATUS.RASCUNHO_ONVIO]: [
      {
         key: 'onvio_draft_done',
         label: 'Rascunho Onvio pronto',
         type: 'checkbox',
      },
   ],
   [ONBOARDING_STATUS.ENVIO_CONTABILIDADE]: [
      {
         key: 'sent_to_accounting_date',
         patchKey: 'sent_to_accounting_at',
         label: 'Enviado à contabilidade em',
         type: 'date',
      },
   ],
};

export function hasManualFields(status) {
   return Boolean(MANUAL_STEP_FIELDS[status]?.length);
}

// Estado do form de UM passo, derivado do onboarding. Datas são DATE-ONLY
// ("YYYY-MM-DD", confirmado na integração: o backend valida esse formato) —
// slice(0,10) cobre tanto "2026-07-15" quanto um eventual ISO completo, SEM
// conversão de fuso (data literal não sofre shift).
export function manualStepDraft(stepKey, onboarding) {
   const draft = {};
   (MANUAL_STEP_FIELDS[stepKey] ?? []).forEach((field) => {
      if (field.type === 'checkbox') {
         draft[field.key] = Boolean(onboarding?.[field.patchKey ?? field.key]);
      } else if (field.type === 'date') {
         const raw = onboarding?.[field.patchKey ?? field.key];
         draft[field.key] = raw ? String(raw).slice(0, 10) : '';
      } else {
         draft[field.key] = onboarding?.[field.key] ?? '';
      }
   });
   return draft;
}

// Patch parcial (só o que mudou vs. baseline). Date vai como "YYYY-MM-DD"
// (formato exigido pelo backend — 422 format com ISO/offset); '' → null
// (limpa o campo).
export function manualStepPatch(stepKey, draft, baseline) {
   const patch = {};
   (MANUAL_STEP_FIELDS[stepKey] ?? []).forEach((field) => {
      if (draft[field.key] === baseline[field.key]) return;
      const patchKey = field.patchKey ?? field.key;
      if (field.type === 'checkbox') {
         patch[patchKey] = draft[field.key];
      } else {
         patch[patchKey] = draft[field.key] || null;
      }
   });
   return patch;
}
